// frontend/src/components/client/mobile/MobileFuelRequests.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Fuel, Calendar, X, RefreshCw, ChevronRight } from 'lucide-react';
import { getFuelRequests, createFuelRequest, cancelFuelRequest, getMyAircraft } from '../../../services/clientApi';

const STATUS = {
  pending:   { label: 'Pending', bg: 'rgba(234,179,8,0.15)',  color: '#eab308' },
  approved:  { label: 'Approved',   bg: 'rgba(34,197,94,0.15)',  color: '#22c55e' },
  completed: { label: 'Completed',    bg: 'rgba(59,130,246,0.15)', color: '#3b82f6' },
  rejected:  { label: 'Rejected',     bg: 'rgba(239,68,68,0.15)',  color: '#ef4444' },
  cancelled: { label: 'Cancelled',     bg: 'rgba(100,116,139,0.15)',color: '#64748b' },
};

const Badge = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

// ─── Bottom Sheet ───────────────────────────────────────────────────────────────
const BottomSheet = ({ open, onClose, title, children }) => {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      />
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: '#1e293b',
        borderRadius: '24px 24px 0 0',
        maxHeight: '92dvh',
        display: 'flex', flexDirection: 'column',
        paddingBottom: 'env(safe-area-inset-bottom)',
        animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1)',
      }}>
        <style>{`@keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }`}</style>
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#334155' }} />
        </div>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 20px 16px' }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9' }}>{title}</span>
          <button onClick={onClose} style={{ background: '#334155', border: 'none', borderRadius: 20, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={15} color="#94a3b8" />
          </button>
        </div>
        {/* Scrollable content */}
        <div style={{ overflowY: 'auto', padding: '0 20px 24px', flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// ─── Form Field ─────────────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 14, padding: '14px 14px', color: '#f1f5f9',
  fontSize: 16, outline: 'none', boxSizing: 'border-box',
  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
};

// ─── Main Component ─────────────────────────────────────────────────────────────
const MobileFuelRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [aircraftList, setAircraftList] = useState([]);
  const [form, setForm] = useState({ aircraft_id: '', quantity: '', scheduled_date: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const intervalRef = useRef(null);

  const loadRequests = useCallback(async (animate = false) => {
    if (animate) setRefreshing(true); else setLoading(true);
    try { const r = await getFuelRequests(); setRequests(r.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => {
    loadRequests();
    getMyAircraft().then(r => setAircraftList(r.data)).catch(() => {});
    intervalRef.current = setInterval(() => loadRequests(), 60000);
    return () => clearInterval(intervalRef.current);
  }, [loadRequests]);

  const handleSubmit = async () => {
    if (!form.aircraft_id || !form.quantity || !form.scheduled_date) {
      alert('Please fill in all required fields'); return;
    }
    setSubmitting(true);
    try {
      await createFuelRequest(form);
      setShowSheet(false);
      setForm({ aircraft_id: '', quantity: '', scheduled_date: '', description: '' });
      await loadRequests();
    } catch { alert('Error creating request'); }
    finally { setSubmitting(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    try { await cancelFuelRequest(id); await loadRequests(); }
    catch { alert('Error cancelling request'); }
  };

  const formatDate = (s) => {
    const d = new Date(s);
    return d.toLocaleDateString('en-US') + ' · ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ padding: '0 16px 100px' }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: '#475569' }}>Auto-refresh · 60s</span>
        <button
          onClick={() => loadRequests(true)}
          style={{ background: '#1e293b', border: '0.5px solid #334155', borderRadius: 10, padding: '8px 10px', cursor: 'pointer' }}
        >
          <RefreshCw size={15} color={refreshing ? '#3b82f6' : '#94a3b8'} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
          <Fuel size={40} color="#334155" style={{ marginBottom: 12 }} />
          <p style={{ color: '#475569', marginBottom: 16 }}>No fuel requests</p>
          <button onClick={() => setShowSheet(true)} style={{ background: '#3b82f6', border: 'none', borderRadius: 12, padding: '10px 20px', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Create a request
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {requests.map((req) => (
            <div key={req.id} style={{ background: '#1e293b', borderRadius: 18, border: '0.5px solid #334155', padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(249,115,22,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Fuel size={18} color="#fb923c" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{req.quantity} gallons</span>
                    <Badge status={req.status} />
                  </div>
                  <p style={{ fontSize: 12, color: '#64748b', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {req.tail_number} – {req.manufacturer} {req.model}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Calendar size={11} color="#475569" />
                    <span style={{ fontSize: 11, color: '#475569' }}>{formatDate(req.scheduled_date)}</span>
                  </div>
                  {req.description && (
                    <p style={{ fontSize: 11, color: '#475569', marginTop: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {req.description}
                    </p>
                  )}
                </div>
                {req.status === 'pending' && (
                  <button
                    onClick={() => handleCancel(req.id)}
                    style={{ background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: 10, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                  >
                    <Trash2 size={15} color="#ef4444" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => setShowSheet(true)}
        style={{
          position: 'fixed', bottom: 'calc(72px + env(safe-area-inset-bottom))', right: 20,
          width: 56, height: 56, borderRadius: 28,
          background: '#3b82f6', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(59,130,246,0.45)',
          zIndex: 50,
        }}
      >
        <Plus size={24} color="#fff" />
      </button>

      {/* Bottom Sheet Form */}
      <BottomSheet open={showSheet} onClose={() => setShowSheet(false)} title="New fuel request">
        <Field label="Aircraft *">
          <select value={form.aircraft_id} onChange={e => setForm({ ...form, aircraft_id: e.target.value })} style={{ ...inputStyle, appearance: 'none' }}>
            <option value="">Select an aircraft</option>
            {aircraftList.map(a => <option key={a.id} value={a.id}>{a.tail_number} – {a.manufacturer} {a.model}</option>)}
          </select>
        </Field>
        <Field label="Quantity (gallons) *">
          <input type="number" inputMode="numeric" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} style={inputStyle} placeholder="Ex: 150" />
        </Field>
        <Field label="Desired date and time *">
          <input type="datetime-local" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} style={inputStyle} />
        </Field>
        <Field label="Description (optional)">
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }} placeholder="Additional information..." />
        </Field>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%', padding: '16px', borderRadius: 16,
            background: submitting ? '#1e40af' : '#3b82f6',
            border: 'none', color: '#fff', fontSize: 16, fontWeight: 600,
            cursor: submitting ? 'default' : 'pointer', marginTop: 8,
          }}
        >
          {submitting ? 'Submitting...' : 'Submit request'}
        </button>
      </BottomSheet>
    </div>
  );
};

export default MobileFuelRequests;